import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SceneUtils } from 'three/examples/jsm/utils/SceneUtils.js';

import pathToModel from '../../assets/models/armfoot.glb';
import texturePath from '../../assets/img/textures/randomlySpacedPoints.jpg';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/f8ffe5-06d6a0-1b9aaa-ef476f-ffc43d');//('https://coolors.co/d5573b-885053-777da7-94c9a9-c6ecae');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[2];
        this.scene.fog = new THREE.Fog(this.colourArr[0], 50, 200);

        this.isCamOrbit = false;

        this.setupCamera();
        this.setupLight();
        this.setupControls();

        this.createSkyboxFromTexture();
        this.setupPlane();
        this.loadModels();
    }

    setupCamera() {
        let VIEW_ANGLE = 90;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.1;
        let FAR_PLANE = 1000;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(-0.1, 10, 80);
    }

    setupLight() {
        let pointLight3 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight3.position.set(0.5, -0.2, 0);
        this.scene.add(pointLight3);

        let hemiLight = new THREE.HemisphereLight(this.colourArr[1], this.colourArr[4], 1);
        hemiLight.position.set(0, 0.5, 0);
        this.scene.add(hemiLight);
    };

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        if (!this.isCamOrbit) this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0.25;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 120;
        this.controls.minDistance = 1;
    }

    setupPlane() {
        this.surfaceArray = [];
        let plane = new THREE.PlaneGeometry(100, 100, 40, 40);
        for (let i = 0; i < plane.vertices.length; i++) {
            //displacement on z
            let value = Math.random() * 40 - 10;
            plane.vertices[i].z = value;
            this.surfaceArray.push(value);
        }
        plane.computeVertexNormals();
        plane.computeFaceNormals();

        let materials = [
            new THREE.MeshPhongMaterial({ color: this.colourArr[2], specular: this.colourArr[0], shading: THREE.FlatShading }),
            new THREE.MeshBasicMaterial({ color: this.colourArr[0], wireframe: true, transparent: true })
        ];

        this.surface = SceneUtils.createMultiMaterialObject(plane, materials);
        this.surface.rotation.x = -Math.PI / 2;
        this.surface.rotation.z = Math.PI;
        this.surface.position.y = -50;
        this.scene.add(this.surface);
    }

    createSkyboxFromTexture() {
        let skyboxMatArr = [];
        let loader = new THREE.TextureLoader();
        let grid = loader.load(texturePath);
        grid.wrapS = THREE.RepeatWrapping;
        grid.wrapT = THREE.RepeatWrapping;
        grid.repeat.set(2, 2);
        grid.anisotropy = 16;

        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[2], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[2], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[3], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[0], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[2], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[1], map: grid }));

        let skyboxGeo = new THREE.BoxBufferGeometry(1, 1, 1);
        skyboxGeo.scale(100, 100, -100);

        let skyboxMesh = new THREE.Mesh(skyboxGeo, skyboxMatArr);
        this.scene.add(skyboxMesh);
    }

    setupMirrorMaterial() {
        this.mirrorSphereCamera = new THREE.CubeCamera(0.01, 100, 512);
        this.scene.add(this.mirrorSphereCamera);
        let mirrorSphereMaterial = new THREE.MeshBasicMaterial({ envMap: this.mirrorSphereCamera.renderTarget.texture });
        return mirrorSphereMaterial;
    }

    updateMirrorMaterial() {
        this.mirrorSphereCamera.update(this.renderer, this.scene);
    }

    loadModels() {
        let gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel,
            (gltf) => {
                gltf.scene.scale.set(0.3, 0.3, 0.3);
                gltf.scene.position.set(0, -9, 30);
                gltf.scene.rotateZ(Math.PI * 0.1);
                this.scene.add(gltf.scene);
            });
        console.log('Loading Complete!');
    }

    orbitCamera(time) {
        //glitch for Stan's video
        // let x = this.camera.position.x;
        // let z = this.camera.position.z;
        // this.camera.position.x = x * Math.cos(time);
        // this.camera.position.z = z * Math.sin(time);
        // this.camera.lookAt(new THREE.Vector3(0,0,0));

        let factor = 30;
        this.camera.position.x = 20 + factor * Math.cos(time);
        this.camera.position.z = 10 + factor * Math.sin(time);
        this.camera.position.y = 40 + factor * Math.sin(time);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    update() {
        if (this.disposed) return;
        if (this.controls) this.controls.update();
        if (this.isCamOrbit) this.orbitCamera(Date.now() * 0.001);
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onKeyDown(e) {
        this.isCamOrbit = true;
    }

    onKeyUp(e) {
        this.isCamOrbit = false;
    }

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
    }
}
